import { getLayoutByElevatorType } from './form'
import { ElevatorTypes } from './options'

/**
 * The set of every field key that appears on ANY elevator-type layout.
 *
 * Built once at module load. Any key in here is "owned" by some layout, so a
 * value for it that is NOT on the current type's layout is a ghost field.
 */
const ALL_LAYOUT_FIELD_KEYS: ReadonlySet<string> = new Set(
  ElevatorTypes.flatMap((type) =>
    getLayoutByElevatorType(type)
      .getAllFields()
      .map((field) => field.key),
  ),
)

/**
 * Removes "ghost" fields from survey data.
 *
 * The survey form is sparse PER elevator type — each type's layout collects
 * only its own fields — but the saved `surveyData` uses the union model. Two
 * flows can leave ghost fields persisted into a saved survey:
 *
 * 1. "Copy Survey" copies the entire source survey's `surveyData`, including
 *    fields the new type's layout never asks about.
 * 2. Picking the wrong elevator type, answering its questions, then switching
 *    type leaves the previously-answered off-type fields in the form.
 *
 * Those ghost values later reach the backend item-selection logic and wrongly
 * exclude maintenance items (including safety-critical tests). Pruning them is
 * the hard guarantee that they can never be persisted.
 *
 * A key is a ghost iff it appears on SOME elevator-type layout but NOT on the
 * current type's layout. This preserves:
 * - Fields truly absent from every layout (system/identity): `unitId`,
 *   `companyId`, `timestamp`, `metadata`, and `elevatorType` (the discriminator).
 * - The current type's own fields — including the common "Basic Info" section
 *   that every layout includes (`siteId`, `surveyor`, `buildingDesignation`,
 *   `startDateMonth`/`startDateYear`, `ahjId`, `manufacturer`, ...). These are
 *   kept because they are on the current layout, NOT because they are absent
 *   from all layouts; if a future layout dropped one, it would be pruned.
 *
 * Pure and idempotent — does not mutate its input, and pruning already-pruned
 * data is a no-op, so layering this guard at multiple call sites is safe.
 *
 * @param surveyData - The survey data to prune.
 * @param elevatorType - The elevator type whose layout defines the valid fields.
 * @returns A new object with ghost fields removed.
 */
export const pruneGhostFields = <T extends Record<string, unknown>>(
  surveyData: T,
  elevatorType: ElevatorTypes,
): T => {
  const thisLayoutFieldKeys = new Set(
    getLayoutByElevatorType(elevatorType)
      .getAllFields()
      .map((field) => field.key),
  )

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(surveyData)) {
    const isGhost =
      ALL_LAYOUT_FIELD_KEYS.has(key) && !thisLayoutFieldKeys.has(key)
    if (!isGhost) {
      result[key] = value
    }
  }

  return result as T
}
